import { get, identity, noop } from 'lodash';
import React, { useState, FC, useEffect } from 'react';
import { Row, Col, Button, Input, Space } from 'antd';

import { NavLink } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { SortDirections } from '../../../shared/constants/sortDirections';
import { Error } from '../../../shared/components/Error';

import { TableStyled } from './styled';

interface IDataGrid {
  query: any;
  columns: any;
  detailsViewPath?: string;
  detailsViewButtonText?: string;
  searchableFieldKey?: string;
  dataMapFn?: (record: any) => { [index: string]: any };
  [key: string]: any;
  queryVariables?: { [key: string]: any };
  queryConfig?: { [key: string]: any };
  actionButtonHandler?: () => void;
  actionButtonText?: string;
}

type Pagination = {
  current: number;
  pageSize: number;
  hideOnSinglePage?: boolean;
  total?: number;
};

/**
 * getQueryLimiters
 */
const getQueryLimiters = (pagination: Pagination, totalItems: number) => {
  let first;
  const pageSize = pagination.pageSize;
  const currentPage = pagination.current;

  first = currentPage * pageSize;

  if (first > totalItems) {
    first = totalItems;
  }

  const last = first - (currentPage - 1) * pageSize;

  return {
    first,
    last,
  };
};

/**
 * getSortOrder
 */
const getSortOrder = ({ field, order }: { field: string; order: string }) => {
  if (order === SortDirections.ASC) {
    return field;
  }

  if (order === SortDirections.DESC) {
    return `-${field}`;
  }

  return '';
};

/**
 * DataGrid
 */
export const DataGrid: FC<IDataGrid> = ({
  query,
  columns,
  detailsViewPath = '',
  detailsViewButtonText = '',
  searchableFieldKey = '',
  dataMapFn = identity,
  filters = [],
  queryVariables = {},
  queryConfig = {},
  actionButtonHandler = noop,
  actionButtonText = '',
  ...rest
}) => {
  /**
   * Search input state
   */
  const [searchInput, setSearchInput] = useState('');

  /**
   * Pagination state
   */
  const [pagination, setPagination] = useState<Pagination>({
    current: 1,
    hideOnSinglePage: true,
    pageSize: 20,
  });

  /**
   * Sorting state
   */
  const [sortDir, setSortDir] = useState('');

  /**
   * Query limiters state
   */
  const [limiters, setLimiters] = useState<{ first: number; last?: number }>({
    first: pagination.pageSize,
  });

  /**
   * Data
   */
  const { data: queryResponseData, loading, error } = useQuery(query.queryBody, {
    variables: {
      ...queryVariables,
      ...(limiters?.first ? { first: limiters.first } : {}),
      ...(limiters?.last ? { last: limiters.last } : {}),
      ...(searchableFieldKey && searchInput ? { [searchableFieldKey]: searchInput } : {}),
      ...(sortDir ? { orderBy: sortDir } : {}),
    },
    fetchPolicy: 'network-only',
    ...queryConfig,
  });

  /**
   * totalCount
   */
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    // Count limiters and totalCount value always based on the most fresh informations from response
    if (
      queryResponseData &&
      queryResponseData[query.queryName] &&
      queryResponseData[query.queryName].totalCount
    ) {
      const totalCount = queryResponseData[query.queryName].totalCount;

      setTotalCount(totalCount);
      setLimiters(getQueryLimiters(pagination, totalCount));
    }
  }, [pagination, query.queryName, queryResponseData]);

  /**
   * Handle error
   */
  if (error) {
    return <Error error={error} />;
  }

  /**
   * onTableChange
   */
  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    setSortDir(getSortOrder(sorter));
    setPagination(pagination);
  };

  /**
   * Data
   */
  const edges = get(queryResponseData, `${query.queryName}.edges`, []);
  const data = edges?.map(dataMapFn);

  const showPaginationTotal = (total: number, range: [number, number]) => (
    <span>
      {range[0]}-{range[1]} von {total}
    </span>
  );

  return (
    <>
      {(searchableFieldKey || detailsViewPath) && (
        <Row gutter={20} style={{ marginBottom: 20 }}>
          {searchableFieldKey && (
            <Col span={12}>
              <Input
                placeholder="Suche"
                value={searchInput}
                onChange={e => {
                  setSearchInput(e.target.value);
                }}
                allowClear
              />
            </Col>
          )}

          {detailsViewPath && (
            <Col span={12} style={{ textAlign: 'right' }}>
              <Space>
                {actionButtonHandler && actionButtonText && (
                  <Button onClick={actionButtonHandler}>{actionButtonText}</Button>
                )}

                <NavLink to={detailsViewPath}>
                  <Button type="primary">{detailsViewButtonText}</Button>
                </NavLink>
              </Space>
            </Col>
          )}
        </Row>
      )}

      <TableStyled
        columns={columns}
        /* @ts-ignore */
        rowKey={record => record.id || record.node?.id}
        dataSource={data}
        pagination={{
          ...pagination,
          total: totalCount,
          position: ['topRight', 'bottomRight'],
          showTotal: showPaginationTotal,
        }}
        loading={loading}
        onChange={onTableChange}
        {...rest}
      />
    </>
  );
};
